import React, { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  updateDoc,
  doc,
  Timestamp,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase/firebase";

import Sidebar from "../components/layout/Sidebar";
import Topbar from "../components/layout/Topbar";

import "./PlanoUsuario.css";

export default function PlanoUsuario() {
  const [usuarios, setUsuarios] = useState([]);
  const [planos, setPlanos] = useState([]);
  const [query, setQuery] = useState("");
  const [filtroUsuarios, setFiltroUsuarios] = useState([]);
  const [modal, setModal] = useState({
    isOpen: false,
    type: "",
    title: "",
    message: "",
    userId: null,
    userName: "",
    planData: null,
  });

  const permissoesFree = {
    ver_mensagens: false,
    ver_sugestoes: true,
    usar_tarot: false,
    usar_ia: false,
    ver_academia: false,
  };

  // Helpers
  const getPlanKeyFromPlano = (plano) => {
    const nome = String(plano?.nome || "").toLowerCase();
    if (nome.includes("diamond")) return "diamond";
    if (nome.includes("premium")) return "premium";
    if (nome.includes("plus")) return "plus";
    return "premium";
  };

  const getPlanLabelFromKey = (key) => {
    const k = String(key || "").toLowerCase().trim();
    if (k === "diamond") return "One Love Diamond";
    if (k === "premium") return "One Love Premium";
    if (k === "plus") return "One Love Plus";
    return "Free";
  };

  const toDateSafe = (value) => {
    if (!value) return null;
    try {
      if (typeof value?.toDate === "function") return value.toDate();
      if (value instanceof Date) return value;
      const d = new Date(value);
      return Number.isNaN(d.getTime()) ? null : d;
    } catch {
      return null;
    }
  };

  const addDays = (baseDate, dias) => {
    return new Date(baseDate.getTime() + dias * 24 * 60 * 60 * 1000);
  };

  const reloadData = async () => {
    try {
      const usersSnap = await getDocs(collection(db, "users"));
      const planosSnap = await getDocs(collection(db, "planos"));

      const usersList = usersSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const planosList = planosSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

      setUsuarios(usersList);
      setPlanos(planosList);
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
    }
  };

  useEffect(() => {
    reloadData();
  }, []);

  useEffect(() => {
    if (!query.trim()) {
      setFiltroUsuarios([]);
      return;
    }
    const lower = query.toLowerCase();
    const filtrados = usuarios.filter(
      (u) =>
        (u.nome && u.nome.toLowerCase().includes(lower)) ||
        (u.email && u.email.toLowerCase().includes(lower))
    );
    setFiltroUsuarios(filtrados);
  }, [query, usuarios]);

  // Modal handlers
  const openModal = (type, userId, userName, planData = null) => {
    const configs = {
      alterar: {
        title: "Alterar Plano",
        message: `Deseja alterar o plano de ${userName} para ${planData?.nome}?`,
      },
      remover: {
        title: "Remover Plano",
        message: `Deseja remover o plano de ${userName}?`,
      },
      renovar: {
        title: "Renovar Plano",
        message: `Deseja renovar o plano de ${userName} por +30 dias?`,
      },
    };
    setModal({
      isOpen: true,
      type,
      ...configs[type],
      userId,
      userName,
      planData,
    });
  };

  const closeModal = () => {
    setModal({
      isOpen: false,
      type: "",
      title: "",
      message: "",
      userId: null,
      userName: "",
      planData: null,
    });
  };

  // Funções principais - NOMES CORRETOS (sem erro de digitação)
  const handleAlterarPlano = async (userId, planData) => {
    try {
      const planKey = getPlanKeyFromPlano(planData);
      const novaData = Timestamp.fromDate(
        new Date(Date.now() + planData.duracao * 24 * 60 * 60 * 1000)
      );

      await updateDoc(doc(db, "users", userId), {
        planoAtual: planData.id,
        planoNome: planData.nome,
        planoExpiraEm: novaData,
        planoAtivo: true,
        permissoes: planData.permissoes || permissoesFree,
        plan: planKey,
        planExpiresAt: novaData,
        lastPaymentStatus: "manual_paid",
        updatedAt: serverTimestamp(),
      });

      alert("✅ Plano atualizado!");
      reloadData();
    } catch (error) {
      alert("❌ Erro: " + error.message);
    }
  };

  const handleRemoverPlano = async (userId) => {
    try {
      await updateDoc(doc(db, "users", userId), {
        planoAtual: null,
        planoNome: "Free",
        planoExpiraEm: null,
        planoAtivo: false,
        permissoes: permissoesFree,
        plan: "free",
        planExpiresAt: null,
        lastPaymentStatus: "expired",
        updatedAt: serverTimestamp(),
      });

      alert("✅ Plano removido!");
      reloadData();
    } catch (error) {
      alert("❌ Erro: " + error.message);
    }
  };

  const handleRenovarPlano = async (userId, userData) => {
    try {
      const currentExpires = toDateSafe(userData?.planExpiresAt) || toDateSafe(userData?.planoExpiraEm);
      const now = new Date();
      const base = currentExpires && currentExpires > now ? currentExpires : now;
      const newExpiresDate = addDays(base, 30);
      const newExpiresTs = Timestamp.fromDate(newExpiresDate);

      await updateDoc(doc(db, "users", userId), {
        planoExpiraEm: newExpiresTs,
        planoAtivo: true,
        planExpiresAt: newExpiresTs,
        lastPaymentStatus: "manual_paid",
        updatedAt: serverTimestamp(),
      });

      alert("✅ Plano renovado por +30 dias!");
      reloadData();
    } catch (error) {
      alert("❌ Erro: " + error.message);
    }
  };

  // Render
  return (
    <div className="plano-usuario-wrapper">
      <Sidebar />
      <div className="main-content">
        <Topbar />
        
        <div className="plano-usuario-content">
          {/* HEADER */}
          <div className="plano-usuario-header">
            <h1>🎯 Gerenciar Planos de Usuários</h1>
            <div className="stats-grid">
              <div className="stat-box">
                <div className="stat-number">{usuarios.length}</div>
                <div className="stat-label">TOTAL USUÁRIOS</div>
              </div>
              <div className="stat-box">
                <div className="stat-number">{usuarios.filter(u => u.plan && u.plan !== "free").length}</div>
                <div className="stat-label">COM PLANO</div>
              </div>
              <div className="stat-box">
                <div className="stat-number">{usuarios.filter(u => !u.plan || u.plan === "free").length}</div>
                <div className="stat-label">FREE</div>
              </div>
            </div>
          </div>

          {/* SEARCH SECTION */}
          <div className="search-container">
            <div className="search-box">
              <i className="fas fa-search"></i>
              <input
                type="text"
                placeholder="Buscar usuário por nome ou e-mail..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="search-input"
              />
              {query && (
                <button className="clear-btn" onClick={() => setQuery("")}>
                  <i className="fas fa-times"></i>
                </button>
              )}
            </div>
            
            <div className="planos-info">
              <span className="planos-label">Planos disponíveis:</span>
              <div className="planos-tags">
                {planos.map((p) => (
                  <span key={p.id} className="plano-tag">
                    {p.nome} - R${p.preco}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* RESULTS */}
          {query && filtroUsuarios.length > 0 ? (
            <div className="table-container">
              <div className="table-responsive">
                <table className="users-table">
                  <thead>
                    <tr>
                      <th>Nome</th>
                      <th>Email</th>
                      <th>Plano Atual</th>
                      <th>Expira em</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtroUsuarios.map((user) => {
                      const expires = user.planoExpiraEm
                        ? new Date(user.planoExpiraEm.seconds * 1000).toLocaleDateString()
                        : user.planExpiresAt?.seconds
                        ? new Date(user.planExpiresAt.seconds * 1000).toLocaleDateString()
                        : "-";

                      const planoLabel = user.planoNome || getPlanLabelFromKey(user.plan) || "Free";
                      const isActive = user.planoAtivo || (user.plan && user.plan !== "free");
                      const isExpired = expires !== "-" && new Date(user.planoExpiraEm?.seconds * 1000) < new Date();

                      return (
                        <tr key={user.id}>
                          <td>
                            <div className="user-cell">
                              <div className="user-name">{user.nome || "-"}</div>
                              <div className="user-id">ID: {user.id.substring(0, 6)}...</div>
                            </div>
                          </td>
                          <td>
                            <div className="user-email">{user.email || "-"}</div>
                          </td>
                          <td>
                            <div className={`plano-badge ${isActive ? "active" : "inactive"}`}>
                              {planoLabel}
                            </div>
                          </td>
                          <td>
                            <div className="expiry-cell">
                              <div className={`expiry-date ${isExpired ? "expired" : ""}`}>
                                {expires}
                              </div>
                              {isExpired && <div className="expired-label">Expirado</div>}
                            </div>
                          </td>
                          <td>
                            <div className="actions-cell">
                              <select
                                className="plano-select"
                                onChange={(e) => {
                                  const selected = planos.find(p => p.id === e.target.value);
                                  if (selected) openModal("alterar", user.id, user.nome, selected);
                                }}
                                defaultValue=""
                              >
                                <option value="" disabled>Alterar Plano</option>
                                {planos.map(p => (
                                  <option key={p.id} value={p.id}>
                                    {p.nome} – R${p.preco}
                                  </option>
                                ))}
                              </select>
                              <button
                                className="btn-renew"
                                onClick={() => openModal("renovar", user.id, user.nome)}
                              >
                                <i className="fas fa-sync-alt"></i> Renovar
                              </button>
                              <button
                                className="btn-remove"
                                onClick={() => openModal("remover", user.id, user.nome)}
                              >
                                <i className="fas fa-trash"></i> Remover
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : query ? (
            <div className="empty-results">
              <i className="fas fa-search"></i>
              <h3>Nenhum usuário encontrado</h3>
              <p>Não encontramos resultados para: <strong>"{query}"</strong></p>
            </div>
          ) : (
            <div className="initial-state">
              <div className="initial-content">
                <i className="fas fa-users"></i>
                <h3>Buscar usuários</h3>
                <p>Digite um nome ou e-mail acima para gerenciar os planos</p>
                <div className="initial-stats">
                  <div className="initial-stat">
                    <strong>{usuarios.length}</strong> usuários no sistema
                  </div>
                  <div className="initial-stat">
                    <strong>{planos.length}</strong> planos disponíveis
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* MODAL */}
        {modal.isOpen && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>{modal.title}</h3>
                <button className="modal-close" onClick={closeModal}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div className="modal-body">
                <p>{modal.message}</p>
                {modal.type === "alterar" && modal.planData && (
                  <div className="plan-details">
                    <div className="detail-row">
                      <span>Plano:</span>
                      <strong>{modal.planData.nome}</strong>
                    </div>
                    <div className="detail-row">
                      <span>Preço:</span>
                      <strong>R${modal.planData.preco}</strong>
                    </div>
                    <div className="detail-row">
                      <span>Duração:</span>
                      <strong>{modal.planData.duracao} dias</strong>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn-cancel" onClick={closeModal}>
                  Cancelar
                </button>
                <button
                  className="btn-confirm"
                  onClick={() => {
                    if (modal.type === "alterar") {
                      handleAlterarPlano(modal.userId, modal.planData);
                    } else if (modal.type === "remover") {
                      handleRemoverPlano(modal.userId);
                    } else if (modal.type === "renovar") {
                      const userData = usuarios.find(u => u.id === modal.userId);
                      handleRenovarPlano(modal.userId, userData);
                    }
                    closeModal();
                  }}
                >
                  Confirmar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
} 